import React from 'react';
import {useParams, Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {formatDistanceToNow, format} from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiGlobe, FiLock, FiClock, FiFile, FiCode, FiImage, FiFileText, FiExternalLink, FiArrowLeft, FiDownload} = FiIcons;

function WorkEntryPage() {
  const {id} = useParams();

  // Mock data - would come from API
  const entry = {
    id: '2',
    title: 'Shipped dashboard redesign',
    summary: 'New layout is live. Reduced cognitive load and improved navigation patterns based on user feedback from last week\'s interviews.',
    timestamp: '2024-01-19T16:45:00Z',
    duration: '5h 30m',
    visibility: 'public',
    user: {
      username: 'alexdoe',
      name: 'Alex Doe'
    },
    actionLog: [
      {time: '14:15', action: 'Started design review session'},
      {time: '14:30', action: 'Analyzed user feedback from interviews'},
      {time: '15:00', action: 'Sketched new navigation patterns'},
      {time: '16:00', action: 'Created high-fidelity mockups in Figma'},
      {time: '17:30', action: 'Implemented responsive layout components'},
      {time: '19:00', action: 'Deployed to production'},
      {time: '19:15', action: 'Verified deployment and user flows'}
    ],
    artifacts: [
      {
        type: 'design',
        name: 'dashboard-v2.figma',
        size: '4.2mb',
        description: 'Complete redesign with improved navigation and reduced cognitive load'
      },
      {
        type: 'code',
        name: 'Dashboard.jsx',
        size: '3.1kb',
        description: 'React component with responsive layout and accessibility improvements'
      },
      {
        type: 'doc',
        name: 'user-feedback-analysis.md',
        size: '2.1kb',
        description: 'Summary of user interview insights that drove the redesign'
      }
    ],
    reflection: 'Really happy with how this turned out. The new navigation feels much more intuitive, and the reduced visual clutter should help users focus on their data. The user feedback was invaluable in identifying the pain points.',
    provenanceLinks: [
      {type: 'built_with', tool: 'Figma', url: 'https://figma.com'},
      {type: 'built_with', tool: 'React', url: 'https://react.dev'},
      {type: 'deployed_to', service: 'Vercel', url: 'https://vercel.com'}
    ]
  };

  const getArtifactIcon = (type) => {
    switch (type) {
      case 'code': return FiCode;
      case 'design': return FiImage;
      case 'doc': return FiFileText;
      default: return FiFile;
    }
  };

  const getVisibilityInfo = (visibility) => {
    return visibility === 'public'
      ? {icon: FiGlobe, color: 'text-indigo-400', label: 'Public'}
      : {icon: FiLock, color: 'text-slate-500', label: 'Private'};
  };

  const visibilityInfo = getVisibilityInfo(entry.visibility);
  const timeAgo = formatDistanceToNow(new Date(entry.timestamp), {addSuffix: true});
  const fullDate = format(new Date(entry.timestamp), 'PPP');

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="min-h-screen bg-slate-950 pt-32 pb-20"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Navigation */}
        <motion.div
          initial={{opacity: 0, x: -20}}
          animate={{opacity: 1, x: 0}}
          className="mb-8"
        >
          <Link
            to={`/${entry.user.username}`}
            className="inline-flex items-center space-x-2 text-slate-400 hover:text-white transition-colors font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            <span>Back to {entry.user.username}'s page</span>
          </Link>
        </motion.div>

        {/* Entry Header */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          className="mb-12"
        >
          <div className="flex items-start justify-between mb-6">
            <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tighter italic uppercase">
              {entry.title}
            </h1>
            <div className={`flex items-center space-x-2 flex-shrink-0 ml-4 text-xs font-black uppercase tracking-widest ${visibilityInfo.color}`}>
              <SafeIcon icon={visibilityInfo.icon} className="w-4 h-4" />
              <span>{visibilityInfo.label}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-6 text-slate-500 mb-8 text-sm font-medium">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiClock} className="w-4 h-4" />
              <span>{entry.duration}</span>
            </div>
            <span>{timeAgo}</span>
            <span>{fullDate}</span>
          </div>
          
          <p className="text-xl text-slate-300 leading-relaxed font-medium">
            {entry.summary}
          </p>
        </motion.div>

        {/* Action Log */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.1}}
          className="mb-12"
        >
          <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-6">
            Session Timeline
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="space-y-4">
              {entry.actionLog.map((log, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <span className="text-sm text-slate-600 font-mono font-bold min-w-[3rem]">
                    {log.time}
                  </span>
                  <span className="text-slate-300 font-medium">
                    {log.action}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Artifacts */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.2}}
          className="mb-12"
        >
          <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-6">
            Artifacts
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {entry.artifacts.map((artifact, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-white/5 p-3 rounded-lg">
                    <SafeIcon icon={getArtifactIcon(artifact.type)} className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-white">
                        {artifact.name}
                      </h3>
                      <span className="text-xs text-slate-500 font-bold">
                        {artifact.size}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mb-4 font-medium">
                      {artifact.description}
                    </p>
                    <button className="flex items-center space-x-2 text-xs font-black text-white uppercase tracking-widest hover:text-indigo-400 transition-colors">
                      <SafeIcon icon={FiDownload} className="w-3 h-3" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Reflection */}
        {entry.reflection && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.3}}
            className="mb-12"
          >
            <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-6">
              Reflection
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <p className="text-slate-300 leading-relaxed italic font-medium text-lg">
                "{entry.reflection}"
              </p>
            </div>
          </motion.div>
        )}

        {/* Provenance Links */}
        {entry.provenanceLinks && entry.provenanceLinks.length > 0 && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.4}}
            className="mb-12"
          >
            <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-6">
              Built With
            </h2>
            <div className="flex flex-wrap gap-3">
              {entry.provenanceLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-sm font-bold text-slate-300 hover:border-white/20 transition-colors"
                >
                  <span>{link.tool}</span>
                  <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default WorkEntryPage;